import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/sidebar';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';

// Routes that support enterprise/multi-location viewing
const ENTERPRISE_COMPATIBLE_ROUTES = [
	'/',
	'/inbox',
	'/conversation',
	'/groupchat',
	'/tags',
	'/locations',
	'/unassigned',
	'/my-messages',
	'/users',
	'/departments',
	'/folders',
	'/activity-feed',
];

/**
 * Checks if the current pathname is an enterprise-compatible route
 * (i.e., a route that supports viewing multiple locations)
 */
const isEnterpriseCompatibleRoute = (pathname: string): boolean => {
	// Root path is enterprise compatible (WebchatDashboard)
	if (pathname === '/') {
		return true;
	}

	return ENTERPRISE_COMPATIBLE_ROUTES.some(
		(route) => route !== '/' && pathname.startsWith(route)
	);
};

/**
 * LocationContextIndicator
 *
 * Shows a visual indicator when a user has "All Locations" selected
 * but is viewing a dashboard that only operates on a single location.
 * This helps users understand which specific business they're viewing/modifying.
 */
export function LocationContextIndicator() {
	const location = useLocation();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const auth = useSelector((state: any) => state.auth);
	const { user } = auth;
	const { state } = useSidebar();
	const isCollapsed = state === 'collapsed';

	// Only show indicator when:
	// 1. User has enterprise enabled (All Locations selected)
	// 2. User is on a non-enterprise-compatible route
	const shouldShowIndicator =
		user?.isEnterpriseEnabled &&
		!isEnterpriseCompatibleRoute(location.pathname);

	if (!shouldShowIndicator) {
		return null;
	}

	const businessName = auth.name || 'Current Location';

	// Collapsed state - just show icon with tooltip
	if (isCollapsed) {
		return (
			<div
				className="flex justify-center py-1"
				title={`Viewing: ${businessName}`}
			>
				<div
					className={cn(
						'flex items-center justify-center',
						'size-6 rounded',
						'bg-amber-100/80 dark:bg-amber-900/40',
						'border border-amber-200/60 dark:border-amber-700/40',
						'text-amber-600 dark:text-amber-400',
						'animate-in fade-in duration-300'
					)}
				>
					<Eye className="size-3" />
				</div>
			</div>
		);
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div
					className={cn(
						'mt-1 overflow-hidden rounded-md cursor-default',
						'bg-gradient-to-r from-amber-50 via-amber-50/80 to-orange-50',
						'dark:from-amber-950/40 dark:via-amber-900/30 dark:to-orange-950/40',
						'border border-amber-200/60 dark:border-amber-700/40',
						'animate-in slide-in-from-top-2 fade-in duration-300'
					)}
				>
					<div className="px-2.5 py-1.5">
						<div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-amber-600/70 dark:text-amber-400/70">
							<Eye className="size-3 shrink-0" />
							<span>Viewing</span>
						</div>
						<p className="mt-0.5 text-xs font-medium truncate text-amber-900 dark:text-amber-100">
							{businessName}
						</p>
					</div>
				</div>
			</TooltipTrigger>
			<TooltipContent side="right" className="max-w-[240px]">
				<p className="font-medium">{businessName}</p>
			</TooltipContent>
		</Tooltip>
	);
}
